import React, { useState, useRef } from 'react';
import { Box, Typography, Alert, Button, Container, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  BandRecommendationFormData as OriginalFormData,
  InstrumentWithPerformer, BandActivityStatus 
} from '../types/BandRecommendations';
import { useAuth } from '../contexts/AuthContext';
import FormSaveHandler from '../components/form/FormSaveHandler';

import RecommenderInfo from './form/RecommenderInfo';
import BandInfo from './form/BandInfo';
import MediaLinks from './form/MediaLinks';
import ContactInfo from './form/ContactInfo';
import RecommendationDetails from './form/RecommendationDetails';
import ExternalValidation from './form/ExternalValidation';
import Availability from './form/Availability';
import ReviewSubmit from './form/ReviewSubmit';
import AdditionalMedia from './form/AdditionalMedia';
import TechnicalRequirements from './form/TechnicalRequirements';
import FinancialInfo from './form/FinancialInfo';
import FormStepper from './FormStepper';

interface BandRecommendationFormData extends OriginalFormData {
  selectedStrand?: string;
  county?: string;
  _id?: string;
}

interface BandRecommendationFormProps {
  initialFormData?: BandRecommendationFormData;
  isEditMode?: boolean;
  formType?: 'recommendation' | 'staff_entry';
  initialStep?: number | string; // number = index (legacy), string = step name
}

const BandRecommendationForm: React.FC<BandRecommendationFormProps> = ({
  initialFormData,
  isEditMode = false,
  formType = 'recommendation',
  initialStep = 0,
}) => {
  const navigate = useNavigate();
  const { user, getToken } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [submitting, setSubmitting] = useState(false);

  // When the user clicks Edit on the review screen and then wants to
  // jump straight back without clicking Next repeatedly
  const [returnToReview, setReturnToReview] = useState(false);

  const defaultFormData: BandRecommendationFormData = {
    selectedStrand: '',
    county: '',
    recommenderName: '',
    recommenderEmail: '',
    recommenderCountry: '',
    recommendedFee: 0,
    isRecommenderApplicant: false,
    artistName: '',
    maleMembers: 0,
    femaleMembers: 0,
    instrumentation: [] as InstrumentWithPerformer[],
    about: '',
    performances: [],
    performanceLink: '',
    youtubeEmbeds: [],
    spotifyEmbeds: [],
    soundcloudEmbeds: [],
    bandcampEmbeds: [],
    musicLinks: [],
    instagramHandle: '',
    twitterHandle: '',
    facebookPage: '',
    tikTokHandle: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    recommendationNotes: '',
    activityStatus: BandActivityStatus.ACTIVE,
    activityStatusNote: '',
    activityStatusDate: new Date(),
    genres: [],
    similarArtists: [],
    availability: {
      from: new Date(),
      to: new Date(),
      excludedDates: [],
    },
    externalValidation: [],
    mainImage: '',
    mainImagePosition: '',
    additionalImages: [],
    additionalImagePositions: [],
    technicalRider: {
      stageSize: '',
      audioRequirements: '',
      lightingRequirements: '',
      additionalEquipment: [],
    },
    financialDetails: {
      minimumFee: 0,
      preferredCurrency: '',
      accommodationNeeded: false,
      transportationNeeded: false,
    },
  };

  // Tracks the draft ID created by FormSaveHandler so final submit promotes it
  const autosavedDraftId = useRef<string | null>(null);

  const [formData, setFormData] = useState<BandRecommendationFormData>(() => {
    if (initialFormData) return { ...defaultFormData, ...initialFormData };
    let base = defaultFormData;
    if (!isEditMode) {
      const savedData = localStorage.getItem('bandRecommendationFormData');
      if (savedData) base = { ...base, ...JSON.parse(savedData) };
      // Reconnect to the existing draft so auto-save uses PUT instead of creating a new record
      const savedDraftId = localStorage.getItem('bandRecommendationDraftId');
      if (savedDraftId) base = { ...base, _id: savedDraftId };
    }
    // For artists, always inject their profile as recommender info so they never have to fill it
    if (user?.role === 'artist') {
      base = {
        ...base,
        recommenderName: user.name || base.recommenderName,
        recommenderEmail: user.email || base.recommenderEmail,
        recommenderCountry: base.recommenderCountry || 'Ireland',
        isRecommenderApplicant: true,
      };
    }
    return base;
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getSteps = () => {
    const isElevated = ['admin', 'superadmin', 'gm'].includes(user?.role || '');
    const baseSteps = [
      ...(isElevated ? ['Page Owner Info'] : []),
      'Band/Artist Information',
      'Contact Information',
      'Categorisation',
      'Availability',
      'Media Links',
      'Additional Media',
      'Press & Awards',
      'Technical Requirements',
      'Financial Information',
    ];
    // Non-elevated, non-artist users (public recommenders) skip the professional detail steps
    const showAdvanced = isEditMode || isElevated || user?.role === 'artist' || formType === 'staff_entry';
    if (!showAdvanced) {
      return [
        'Band/Artist Information',
        'Contact Information',
        'Categorisation',
        'Availability',
        'Media Links',
        'Press & Awards',
        'Review & Submit',
      ];
    }
    return [...baseSteps, 'Review & Submit'];
  };

  const steps = getSteps();
  const reviewStepIndex = steps.length - 1;

  const [activeStep, setActiveStep] = useState<number>(() => {
    const s = getSteps();
    if (typeof initialStep === 'string' && isNaN(Number(initialStep))) {
      const idx = s.indexOf(initialStep);
      return idx >= 0 ? idx : 0;
    }
    return Number(initialStep) || 0;
  });

  // Called when user clicks Edit on the review screen.
  // Sets returnToReview so a "Back to Review" button appears on that step.
  const handleNavigateFromReview = (stepIndex: number) => {
    setReturnToReview(true);
    setActiveStep(stepIndex);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const getStepComponent = (stepName: string) => {
    const commonProps = { formData, setFormData };
    const componentMap: { [key: string]: React.ReactNode } = {
      'Page Owner Info': <RecommenderInfo {...commonProps} />,
      'Band/Artist Information': <BandInfo {...commonProps} />,
      'Media Links': <MediaLinks {...commonProps} />,
      'Contact Information': <ContactInfo {...commonProps} />,
      'Categorisation': <RecommendationDetails {...commonProps} />,
      'Availability': <Availability {...commonProps} />,
      'Press & Awards': (
        <ExternalValidation
          value={formData.externalValidation || []}
          onChange={(entries) => handleChange({ 
            target: { 
              name: 'externalValidation', 
              value: entries 
            } 
          })}
        />
      ),
      'Additional Media': <AdditionalMedia {...commonProps} />,
      'Technical Requirements': <TechnicalRequirements {...commonProps} />,
      'Financial Information': <FinancialInfo {...commonProps} />,
      'Review & Submit': (
        <ReviewSubmit
          formData={formData}
          onSubmit={handleSubmit}
          isEditMode={isEditMode}
          onNavigate={handleNavigateFromReview}
          formType={formType}
          steps={steps}
        />
      ),
    };
    return componentMap[stepName] || <Typography>Unknown step: {stepName}</Typography>;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      const token = await getToken();
      if (!token) { setErrorMessage('You must be logged in to submit.'); return; }

      const baseData = { ...formData, createdBy: user?.role || 'unknown', userId: user?._id || null, userRole: user?.role || null, formType };
      let dataToSubmit: any = { ...baseData };

      if (formType === 'staff_entry' && !baseData.recommenderName) {
        const { recommenderName, recommenderEmail, recommenderCountry, isRecommenderApplicant, ...cleaned } = baseData;
        dataToSubmit = cleaned;
      }
      if (!dataToSubmit.selectedStrand) {
        const { selectedStrand, ...cleaned } = dataToSubmit;
        dataToSubmit = cleaned;
      }

      const config = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, withCredentials: true };

      if (isEditMode && formData._id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/bandrecommendations/${formData._id}`, dataToSubmit, config);
        setSuccessMessage('Band information updated successfully!');
        navigate(`/band/${formData._id}`);
      } else {
        // If FormSaveHandler already created a draft, promote it via the submit endpoint
        const existingDraftId = formData._id || autosavedDraftId.current;
        if (existingDraftId) {
          // First save any latest changes to the draft, then submit it
          await axios.put(`${process.env.REACT_APP_API_URL}/api/bandrecommendations/${existingDraftId}`, dataToSubmit, config);
          await axios.patch(`${process.env.REACT_APP_API_URL}/api/bandrecommendations/${existingDraftId}/submit`, {}, config);
          setSuccessMessage(formType === 'staff_entry' ? 'Band added to database successfully!' : 'Your band has been submitted for review. Allow up to 24 hours (48 hours over weekends) for approval.');
          localStorage.removeItem('bandRecommendationFormData');
          localStorage.removeItem('bandRecommendationDraftId');
          navigate(`/band/${existingDraftId}`);
        } else {
          // No draft was auto-saved — create directly as pending (not draft)
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/bandrecommendations`, { ...dataToSubmit, isDraft: false, status: 'pending' }, config);
          setSuccessMessage(formType === 'staff_entry' ? 'Band added to database successfully!' : 'Your band has been submitted for review. Allow up to 24 hours (48 hours over weekends) for approval.');
          localStorage.removeItem('bandRecommendationFormData');
          localStorage.removeItem('bandRecommendationDraftId');
          navigate(`/band/${response.data._id}`);
        }
      }
    } catch (error: any) {
      let msg = formType === 'staff_entry' ? 'Failed to add band.' : 'Failed to submit recommendation.';
      if (error.response) {
        switch (error.response.status) {
          case 401: msg = 'Your session has expired. Please log in again.'; break;
          case 403: msg = 'You do not have permission to perform this action.'; break;
          case 400: {
            const raw = error.response.data.message || '';
            // Dynamically extract field names from Mongoose validation error
            const fieldMatches = raw.match(/Path `([^`]+)` is required/g) || [];
            const missingFields = fieldMatches.map((match: string) => {
              const field = match.replace("Path `", '').replace('` is required', '');
              return field.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase()).trim();
            });
            msg = missingFields.length > 0
              ? `Please complete the following required fields before saving:\n\n• ${missingFields.join('\n• ')}`
              : raw || 'Invalid form data. Please check your inputs.';
            break;
          }
          default:  msg = error.response.data.message || msg;
        }
      }
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (activeStep === reviewStepIndex) { handleSubmit(); }
    else { setActiveStep(prev => prev + 1); }
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  // Jump straight back to the review step and clear the flag
  const handleBackToReview = () => {
    setReturnToReview(false);
    setActiveStep(reviewStepIndex);
  };

  const isStepValid = () => {
    switch (steps[activeStep]) {
      case 'Band/Artist Information':
        return formData.artistName && formData.about;
      case 'Contact Information':
        return formData.contactName && formData.contactEmail;
      default:
        return true;
    }
  };

  const getFormTitle = () => {
    if (isEditMode) return 'Edit Artist/Band Information';
    return formType === 'staff_entry' ? 'Add Band to Database' : 'Submit Artist/Band Recommendation';
  };

  const isOnReviewStep = activeStep === reviewStepIndex;

  return (
    <Container maxWidth={isMobile ? 'sm' : 'md'}>
      <FormSaveHandler
        formData={formData}
        formId={formData._id}
        isEditMode={isEditMode}
        enabled={!submitting}
        onDraftSaved={(id) => { autosavedDraftId.current = id; }}
      />

      <Box sx={{ mt: isMobile ? 2 : 4, mb: 8 }}>
        <Box sx={{ position: 'relative', textAlign: 'center', mb: isMobile ? 2 : 4 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom color="primary.main">
            {getFormTitle()}
          </Typography>
          {isEditMode && formData._id && (
            <Tooltip title="Open the live band page in a new tab">
              <Button
                component={Link}
                to={`/band/${formData._id}`}
                size="small"
                variant="outlined"
                sx={{ position: { sm: 'absolute' }, right: 0, top: '50%', transform: { sm: 'translateY(-50%)' }, mt: { xs: 1, sm: 0 } }}
              >
                View Live Page ↗
              </Button>
            </Tooltip>
          )}
        </Box>

        {isMobile && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
        )}

        {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}
        {errorMessage   && <Alert severity="error"   sx={{ mb: 3 }}>{errorMessage}</Alert>}

        <FormStepper steps={steps} activeStep={activeStep} setActiveStep={setActiveStep} />

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          {getStepComponent(steps[activeStep])}
        </Box>

        <Box sx={{
          display: 'flex', justifyContent: 'space-between', p: isMobile ? 2 : 3,
          flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0,
          alignItems: 'center',
        }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || submitting}
            fullWidth={isMobile}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
            {/* Back to Review button — only visible when user came from the review screen */}
            {returnToReview && !isOnReviewStep && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleBackToReview}
                fullWidth={isMobile}
              >
                ← Back to Review
              </Button>
            )}

            {!isOnReviewStep && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={submitting || !isStepValid()}
                fullWidth={isMobile}
              >
                Next
              </Button>
            )}

          </Box>
        </Box>

        {isMobile && (
          <Box sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider',
            p: 2, display: 'flex', justifyContent: 'center'
          }}>
            <Typography variant="caption" color="text.secondary">
              {steps[activeStep]} • {activeStep + 1} of {steps.length}
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default BandRecommendationForm;